import type { Polygon } from 'geojson';

/**
 * Spherical-earth geodesy helpers for the radial measuring tool. Pure math (no
 * MapLibre dependency) so they stay trivially unit-testable. Uses the same mean
 * earth radius as Leaflet's CRS.Earth so measurements agree with the legacy
 * portals' ruler.
 *
 * Valid at any latitude and longitude — the formulas are fully spherical, so
 * accuracy is the same for every deployment region (within ~0.5% of the WGS84
 * ellipsoid, like the legacy ruler). The one unhandled edge is the antimeridian
 * (±180° longitude): a circle straddling it emits un-normalised longitudes,
 * which no current or planned deployment region crosses.
 */
const EARTH_RADIUS_METERS = 6_371_000;

const toRadians = (degrees: number) => (degrees * Math.PI) / 180;
const toDegrees = (radians: number) => (radians * 180) / Math.PI;

/**
 * Great-circle (haversine) distance in metres between two [longitude, latitude]
 * positions.
 */
export function haversineDistanceMeters(a: [number, number], b: [number, number]): number {
    const lat1 = toRadians(a[1]);
    const lat2 = toRadians(b[1]);
    const dLat = toRadians(b[1] - a[1]);
    const dLng = toRadians(b[0] - a[0]);

    const h =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 2 * EARTH_RADIUS_METERS * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * The [longitude, latitude] position reached by travelling `distanceMeters` from
 * `origin` along the given initial bearing (degrees clockwise from north), using
 * the spherical direct formula — accurate at any latitude, unlike a flat-earth
 * degrees-per-metre approximation.
 */
export function destinationPoint(
    origin: [number, number],
    distanceMeters: number,
    bearingDegrees: number,
): [number, number] {
    const lat1 = toRadians(origin[1]);
    const lng1 = toRadians(origin[0]);
    const bearing = toRadians(bearingDegrees);
    const angular = distanceMeters / EARTH_RADIUS_METERS;

    const lat2 = Math.asin(Math.sin(lat1) * Math.cos(angular) + Math.cos(lat1) * Math.sin(angular) * Math.cos(bearing));
    const lng2 =
        lng1 +
        Math.atan2(
            Math.sin(bearing) * Math.sin(angular) * Math.cos(lat1),
            Math.cos(angular) - Math.sin(lat1) * Math.sin(lat2),
        );
    return [toDegrees(lng2), toDegrees(lat2)];
}

/**
 * A GeoJSON Polygon approximating the circle of geographic radius `radiusMeters`
 * around `centre`, built from {@link destinationPoint} at `steps` equally spaced
 * bearings. The ring is closed (first position repeated last), per the GeoJSON
 * spec. 64 steps renders smoothly at every radius the tool can produce.
 */
export function circlePolygon(centre: [number, number], radiusMeters: number, steps = 64): Polygon {
    const ring: [number, number][] = [];
    for (let i = 0; i < steps; i++) {
        ring.push(destinationPoint(centre, radiusMeters, (i * 360) / steps));
    }
    ring.push(ring[0]);
    return { type: 'Polygon', coordinates: [ring] };
}
